import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import {
  emptyProgress,
  morseLetterId,
  newId,
  type LetterProgress,
  type MorseMutation,
  type ProgressMap,
} from '@familyhub/shared';
import { stamp } from '../platform/clock';
import { registerCleanup } from '../platform/cleanup';
import { mdb, type MorseLetterRecord } from './db';
import { morseSync } from './sync';

/**
 * The morse write path: apply locally, queue the mutation, let the runner
 * replay it — the route every module's writes take.
 *
 * **An answer is a read-modify-write on a row tapped every couple of seconds**,
 * which is the case CLAUDE.md warns about: a Dexie write is not reliably visible
 * to the next read, and LWW is per row, so a stale read would roll back the
 * previous answer's box *and* its counters. So the last row this phone wrote is
 * also held in memory (`written`) and `useProgress` overlays it on what Dexie
 * returns, newest `updated_at` winning — the same remedy as the list's `readRow`.
 */

const written = new Map<string, MorseLetterRecord>();
// Logout wipes the store; the overlay must not outlive it.
registerCleanup(async () => written.clear());

function newer(a: MorseLetterRecord | undefined, b: MorseLetterRecord | undefined) {
  if (!a) return b;
  if (!b) return a;
  return a.updated_at >= b.updated_at ? a : b;
}

function toProgress(r: MorseLetterRecord): LetterProgress {
  return {
    letter: r.letter,
    box: r.box,
    due_at: r.due_at,
    correct: r.correct,
    wrong: r.wrong,
    word_hits: r.word_hits,
    introduced_at: r.introduced_at,
    mastered_at: r.mastered_at,
    mnemonic: r.mnemonic,
  };
}

/** This learner's progress, letter → state. Undefined while Dexie loads *or
 *  the session has not resolved* — a lesson started before anyone is known
 *  would write rows owned by nobody. */
export function useProgress(userId: string | null): ProgressMap | undefined {
  // Tagged with the user it was read for: `useLiveQuery` keeps returning the
  // *previous* query's answer while a new one loads, so the empty result read
  // before the session resolved would otherwise pass for "nothing learned yet"
  // for a moment — and a tap on "Begin" in that moment would overwrite every
  // real row with a fresh one.
  const read = useLiveQuery(
    async () => ({
      userId,
      rows: userId ? await mdb.letters.where('created_by').equals(userId).toArray() : [],
    }),
    [userId],
  );
  return useMemo(() => {
    if (!read || !userId || read.userId !== userId) return undefined;
    const byId = new Map<string, MorseLetterRecord>();
    for (const r of read.rows) byId.set(r.id, r);
    for (const r of written.values()) if (r.created_by === userId) byId.set(r.id, newer(byId.get(r.id), r)!);
    const map: Record<string, LetterProgress> = {};
    for (const r of byId.values()) if (!r.deleted_at) map[r.letter] = toProgress(r);
    return map;
  }, [read, userId]);
}

/** Write one letter's new state. */
export async function saveLetter(userId: string, p: LetterProgress): Promise<void> {
  const id = morseLetterId(userId, p.letter);
  const previous = newer(await mdb.letters.get(id), written.get(id));
  const record: MorseLetterRecord = {
    id,
    ...p,
    created_by: userId,
    created_at: previous?.created_at,
    updated_at: stamp(),
    deleted_at: null,
  };
  written.set(id, record);
  const mutation = { op: 'upsert', table: 'morse_letters', id: newId(), row: record } as unknown as MorseMutation;
  await mdb.transaction('rw', [mdb.letters, mdb.outbox], async () => {
    await mdb.letters.put(record);
    await mdb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void morseSync.sync();
}

/** Save several letters (a word touches each of its letters). */
export async function saveLetters(userId: string, ps: LetterProgress[]): Promise<void> {
  for (const p of ps) await saveLetter(userId, p);
}

export const progressOf = (map: ProgressMap, letter: string): LetterProgress =>
  map[letter] ?? emptyProgress(letter);
